import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { prepareDocumentSignature, addDocumentSignature } from './documentSignature';

// Detectar iOS/Safari para tratamento especial de downloads
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// Função de download compatível com iOS/Safari
export const downloadPDFCrossBrowser = (doc, filename) => {
  return new Promise((resolve, reject) => {
    try {
      if (isIOS() || isSafari()) {
        // iOS/Safari: usar data URI e abrir em nova aba
        // Isso é mais confiável que Blob no Safari mobile
        const pdfOutput = doc.output('datauristring');
        
        // Criar link e simular click
        const link = document.createElement('a');
        link.href = pdfOutput;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // No iOS, setTimeout ajuda a garantir que o click seja processado
        setTimeout(() => {
          link.click();
          document.body.removeChild(link);
          
          // Dar tempo para o download iniciar antes de resolver
          setTimeout(() => {
            resolve(doc);
          }, 500);
        }, 100);
        
      } else {
        // Chrome/Android: usar método padrão do jsPDF
        doc.save(filename);
        resolve(doc);
      }
    } catch (error) {
      console.error('Erro no download do PDF:', error);
      // Fallback: tentar método padrão
      try {
        doc.save(filename);
        resolve(doc);
      } catch (e) {
        reject(e);
      }
    }
  });
};

// Logo URL - usando base64 inline para evitar problemas de CORS
// Esta é uma versão simplificada do logo em base64
const LOGO_FALLBACK_BASE64 = null; // Será carregado da URL se possível

// Dados da empresa para os PDFs
export const EMPRESA = {
  nome: 'Instituto Educacional e Clínico InterEquipe',
  cnpj: '11.296.242/0001-90',
  endereco: 'Rua Profª Mary Dora D\'Arienzo Favoretto, 201',
  bairro: 'Vila Trujilo',
  cidade: 'Sorocaba/SP',
  cep: '18060-400',
  telefone: '(15) 3326-4454',
  whatsapp: '(15) 98833-4427',
  email: 'contato@interequipe.com.br'
};

// Cache do logo em base64
let logoBase64Cache = null;

// Limpar cache do logo (para forçar recarregamento)
export const clearLogoCache = () => {
  logoBase64Cache = null;
};

export const loadLogo = async () => {
  if (logoBase64Cache) return logoBase64Cache;
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  try {
    // Primeiro, tentar carregar via API backend (proxy para evitar CORS)
    const res = await fetch(`${API_BASE}/api/assets/logo`);
    const data = await res.json();
    if (data.logo) {
      logoBase64Cache = data.logo;
      console.log('Logo carregado com sucesso via proxy, tamanho:', (data.logo.length/1024).toFixed(0), 'KB');
      return logoBase64Cache;
    }
  } catch (err) {
    console.warn('Erro ao carregar logo via proxy, tentando direto:', err);
  }
  
  // Fallback: Logo URL direto
  const LOGO_URL = 'https://customer-assets.emergentagent.com/job_clinical-scheduler/artifacts/dh1ga4hw_LOGO_interequipe-removebg_sem%20nome.png';
  
  try {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          // Redimensionar logo para tamanho menor (max 150px)
          const maxSize = 150;
          let width = img.width;
          let height = img.height;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          // Usar PNG para manter transparência, mas com tamanho reduzido
          logoBase64Cache = canvas.toDataURL('image/png');
          console.log('Logo carregado e redimensionado:', (logoBase64Cache.length/1024).toFixed(0), 'KB');
          resolve(logoBase64Cache);
        } catch (e) {
          console.warn('Erro ao converter logo:', e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.warn('Não foi possível carregar o logo');
        resolve(null);
      };
      img.src = LOGO_URL;
    });
  } catch (err) {
    console.warn('Erro geral ao carregar logo:', err);
    return null;
  }
};

export const addHeaderToPDF = (doc, logoBase64) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Logo (se disponível)
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', 15, 10, 30, 30);
    } catch (e) {
      console.error('Erro ao adicionar logo:', e);
    }
  }
  
  // Dados da empresa
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(EMPRESA.nome, pageWidth / 2, 15, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`CNPJ: ${EMPRESA.cnpj}`, pageWidth / 2, 22, { align: 'center' });
  doc.text(`${EMPRESA.endereco}, ${EMPRESA.bairro}`, pageWidth / 2, 28, { align: 'center' });
  doc.text(`${EMPRESA.cidade} - CEP: ${EMPRESA.cep}`, pageWidth / 2, 34, { align: 'center' });
  doc.text(`Tel: ${EMPRESA.telefone} | WhatsApp: ${EMPRESA.whatsapp}`, pageWidth / 2, 40, { align: 'center' });
  
  // Linha separadora
  doc.setDrawColor(74, 107, 93); // #4A6B5D
  doc.setLineWidth(0.5);
  doc.line(15, 46, pageWidth - 15, 46);
  
  return 56; // Retorna Y inicial para o conteúdo
};

export const addFooterToPDF = (doc, profissionalNome) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Linha de assinatura
  const sigY = pageHeight - 50;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(pageWidth / 2 - 50, sigY, pageWidth / 2 + 50, sigY);
  
  doc.setFontSize(10);
  if (profissionalNome) {
    doc.text(profissionalNome, pageWidth / 2, sigY + 6, { align: 'center' });
    doc.text('Profissional Responsável', pageWidth / 2, sigY + 12, { align: 'center' });
  } else {
    doc.text('Assinatura e Carimbo do Profissional', pageWidth / 2, sigY + 6, { align: 'center' });
  }
  
  // Data e local
  doc.setFontSize(9);
  doc.text(`Sorocaba/SP, ${format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`, pageWidth / 2, pageHeight - 25, { align: 'center' });
};

// Função para comprimir imagem base64 (reduz tamanho da assinatura)
const compressImageBase64 = (base64, maxWidth = 200, quality = 0.4) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Redimensionar se muito grande
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Fundo branco para melhor compressão JPEG
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      // Converter para JPEG com compressão agressiva
      const compressed = canvas.toDataURL('image/jpeg', quality);
      console.log(`Assinatura comprimida: ${(base64.length/1024).toFixed(0)}KB -> ${(compressed.length/1024).toFixed(0)}KB`);
      resolve(compressed);
    };
    img.onerror = () => resolve(base64); // Fallback: usar original
    img.src = base64;
  });
};

// Footer com rubrica gráfica. Não é certificação digital.
export const addFooterWithSignatureToPDF = async (doc, profissional) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  let sigY = pageHeight - 60;
  
  // Assinatura ausente/falha não pode passar silenciosamente no fluxo profissional.
  if (profissional?.assinaturaObrigatoria && !profissional?.assinatura) {
    throw new Error('Cadastre sua assinatura em Configurações antes de emitir o termo.');
  }
  if (profissional?.assinatura) {
    const png = await prepareDocumentSignature(profissional.assinatura);
    sigY += addDocumentSignature(doc, png, sigY) + 4;
  } else {
    // Linha de assinatura tradicional
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(pageWidth / 2 - 50, sigY, pageWidth / 2 + 50, sigY);
    sigY += 5;
  }
  
  // Nome do profissional
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (profissional?.nome) {
    doc.text(profissional.nome, pageWidth / 2, sigY, { align: 'center' });
    sigY += 5;
  }
  
  // Especialidade (se disponível)
  if (profissional?.especialidade) {
    doc.setFontSize(9);
    doc.text(profissional.especialidade, pageWidth / 2, sigY, { align: 'center' });
    sigY += 4;
  }
  
  // CRM/CRP (se disponível)
  if (profissional?.crm_crp) {
    doc.setFontSize(9);
    doc.text(profissional.crm_crp, pageWidth / 2, sigY, { align: 'center' });
    sigY += 4;
  }
  
  // Label "Profissional Responsável"
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('Profissional Responsável', pageWidth / 2, sigY + 3, { align: 'center' });
  
  // Data e local
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Sorocaba/SP, ${format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
};

export const generateTermoDesligamento = async (dados) => {
  const { paciente, profissional, motivo, encaminhamento, dataUltimoAtendimento } = dados;
  if (dados.realizadoPor === 'profissional' && !profissional?.assinatura) {
    throw new Error('Cadastre sua assinatura em Configurações antes de emitir o termo.');
  }
  
  const logoBase64 = await loadLogo();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = addHeaderToPDF(doc, logoBase64);

  // Título
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMO DE DESLIGAMENTO TERAPÊUTICO', pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Corpo do documento
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  const texto1 = `Declaro para os devidos fins que o(a) paciente ${paciente.nome_completo.toUpperCase()}, ` +
    `esteve em acompanhamento terapêutico nesta instituição e que, a partir desta data, ` +
    `encontra-se DESLIGADO(A) do tratamento.`;
  
  const lines1 = doc.splitTextToSize(texto1, pageWidth - 40);
  doc.text(lines1, 20, y);
  y += lines1.length * 7 + 10;

  // Data do último atendimento
  if (dataUltimoAtendimento) {
    doc.text(`Data do último atendimento: ${format(new Date(dataUltimoAtendimento), "dd/MM/yyyy", { locale: ptBR })}`, 20, y);
    y += 10;
  }

  // Motivo
  doc.setFont('helvetica', 'bold');
  doc.text('Motivo do desligamento:', 20, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  const motivoLines = doc.splitTextToSize(motivo || 'Não informado', pageWidth - 40);
  doc.text(motivoLines, 20, y);
  y += motivoLines.length * 7 + 10;

  // Encaminhamento (se houver)
  if (encaminhamento) {
    doc.setFont('helvetica', 'bold');
    doc.text('Encaminhamento/Orientações:', 20, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    const encLines = doc.splitTextToSize(encaminhamento, pageWidth - 40);
    doc.text(encLines, 20, y);
    y += encLines.length * 7 + 10;
  }

  // Observação legal
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  const obsLegal = 'Este documento não substitui o prontuário do paciente, que permanece arquivado na instituição conforme legislação vigente.';
  const obsLines = doc.splitTextToSize(obsLegal, pageWidth - 40);
  doc.text(obsLines, 20, y);

  // Usar footer com suporte a assinatura digital (agora é async)
  await addFooterWithSignatureToPDF(doc, profissional);
  
  return doc;
};

export const downloadTermoDesligamento = async (dados) => {
  const doc = await generateTermoDesligamento(dados);
  const filename = `termo_desligamento_${dados.paciente.nome_completo.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
  
  // Usar função de download compatível com iOS/Safari
  await downloadPDFCrossBrowser(doc, filename);
  return doc;
};

export const getTermoDesligamentoBase64 = async (dados) => {
  const doc = await generateTermoDesligamento(dados);
  return doc.output('datauristring');
};
